import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Loader2, Save, ClipboardList, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Substances list in alphabetical order
const SUBSTANCES_OPTIONS = [
  'Alcohol',
  'Club Drugs',
  'Cocaine',
  'Hallucinogens',
  'Inhalants',
  'Marijuana/Hashish',
  'Methamphetamine',
  'Opiates/Narcotics',
  'OxyContin',
  'Prescription Medication',
  'Sedatives/Hypnotics',
  'Stimulants/Amphetamines',
  'Other'
];

// Observed indicators list in alphabetical order
const OBSERVED_INDICATORS_OPTIONS = [
  'Agitation',
  'Burns on inside of lips (from freebasing cocaine)',
  'Dilated (enlarged) or constricted (pinpoint) pupils',
  'Drug paraphernalia (pipes/papers/needles/roach clips)',
  'Inability to focus',
  'Needle track marks',
  '"Nodding off" (dozing or falling asleep)',
  'Scratching',
  'Skin abscesses, cigarette burns, or nicotine stains',
  'Smell of alcohol or marijuana',
  'Stimulants/Amphetamines',
  'Swollen hands or feet',
  'Tremors (shaking and twitching of hands/eyelids)',
  'Unclear speech (slurred, incoherent, or too rapid)',
  'Unsteady gait (staggering, off balance)'
];

const AddictionsScreeningInstrumentCard = ({ caseId, caseData, onUpdate }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [substancesUsed, setSubstancesUsed] = useState([]);
  const [substancesOtherText, setSubstancesOtherText] = useState('');
  const [observedIndicators, setObservedIndicators] = useState([]);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [updatedByName, setUpdatedByName] = useState('');

  // Check if this is an Addictions Recovery case
  const isAddictionsRecoveryCase = 
    caseData?.program_name?.toLowerCase().includes('addictions') ||
    caseData?.program_type?.toLowerCase().includes('addictions');

  useEffect(() => {
    if (isAddictionsRecoveryCase && caseId) {
      fetchScreeningInstrument();
    } else {
      setLoading(false);
    }
  }, [caseId, isAddictionsRecoveryCase]);

  const fetchScreeningInstrument = async () => {
    try {
      const response = await axios.get(`${API}/cases/${caseId}/screening-instrument`);
      setSubstancesUsed(response.data.substances_used || []);
      setSubstancesOtherText(response.data.substances_other_text || '');
      setObservedIndicators(response.data.observed_indicators || []);
      setLastUpdated(response.data.last_updated);
      setUpdatedByName(response.data.updated_by_name);
    } catch (error) {
      console.error('Failed to fetch screening instrument:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubstanceChange = (substance, checked) => {
    if (checked) {
      setSubstancesUsed(prev => [...prev, substance]);
    } else {
      setSubstancesUsed(prev => prev.filter(s => s !== substance));
      // Clear other text if "Other" is unchecked
      if (substance === 'Other') {
        setSubstancesOtherText('');
      }
    }
  };

  const handleIndicatorChange = (indicator, checked) => {
    if (checked) {
      setObservedIndicators(prev => [...prev, indicator]);
    } else {
      setObservedIndicators(prev => prev.filter(i => i !== indicator));
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/cases/${caseId}/screening-instrument`, {
        substances_used: substancesUsed,
        substances_other_text: substancesUsed.includes('Other') ? substancesOtherText : '',
        observed_indicators: observedIndicators
      });
      toast.success('Screening Instrument saved successfully');
      fetchScreeningInstrument(); // Refresh to get updated metadata
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save Screening Instrument');
    } finally {
      setSaving(false);
    }
  };

  // Don't render if not an Addictions Recovery case
  if (!isAddictionsRecoveryCase) {
    return null;
  }

  if (loading) {
    return (
      <Card className="card-shadow" data-testid="screening-instrument-card">
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </CardContent>
      </Card>
    );
  }

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <Card className="card-shadow col-span-2" data-testid="screening-instrument-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-primary" />
            <CardTitle className="text-lg">Screening Instrument</CardTitle>
          </div>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={saving}
            data-testid="save-screening-instrument-btn"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Save
          </Button>
        </div>
        <CardDescription>
          Document substances used and observed indicators for this participant.
          {lastUpdated && (
            <span className="block mt-1 text-xs">
              Last updated: {formatDateTime(lastUpdated)} by {updatedByName}
            </span>
          )}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Substances Used Section */}
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-sm uppercase tracking-wide text-muted-foreground">
              Substances Used
            </h3>
            {substancesUsed.length > 0 && (
              <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                {substancesUsed.length} selected
              </span>
            )}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {SUBSTANCES_OPTIONS.map((substance) => (
              <div key={substance} className="flex items-center space-x-2">
                <Checkbox
                  id={`substance-${substance}`}
                  checked={substancesUsed.includes(substance)}
                  onCheckedChange={(checked) => handleSubstanceChange(substance, checked)}
                  data-testid={`substance-checkbox-${substance.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
                />
                <Label
                  htmlFor={`substance-${substance}`}
                  className="text-sm font-normal cursor-pointer"
                >
                  {substance}
                </Label>
              </div>
            ))}
          </div>
          
          {/* Other free text field */}
          {substancesUsed.includes('Other') && (
            <div className="ml-6 mt-2">
              <Label htmlFor="other-substance-text" className="text-sm text-muted-foreground">
                Please specify:
              </Label>
              <Input
                id="other-substance-text"
                value={substancesOtherText}
                onChange={(e) => setSubstancesOtherText(e.target.value)}
                placeholder="Enter other substance(s)..."
                className="mt-1 max-w-md"
                data-testid="substance-other-text-input"
              />
            </div>
          )}
        </div>

        {/* Divider */}
        <div className="border-t border-border" />

        {/* Observed Indicators Section */}
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-sm uppercase tracking-wide text-muted-foreground">
              Observed Indicators
            </h3>
            {observedIndicators.length > 0 && (
              <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full">
                {observedIndicators.length} selected
              </span>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {OBSERVED_INDICATORS_OPTIONS.map((indicator) => (
              <div key={indicator} className="flex items-start space-x-2">
                <Checkbox
                  id={`indicator-${indicator}`}
                  checked={observedIndicators.includes(indicator)}
                  onCheckedChange={(checked) => handleIndicatorChange(indicator, checked)}
                  className="mt-0.5"
                  data-testid={`indicator-checkbox-${indicator.toLowerCase().replace(/[^a-z0-9]/g, '-').substring(0, 30)}`}
                />
                <Label
                  htmlFor={`indicator-${indicator}`}
                  className="text-sm font-normal cursor-pointer leading-tight"
                >
                  {indicator}
                </Label>
              </div>
            ))}
          </div>
        </div>

        {/* Merge Field Info */}
        <div className="bg-muted/50 rounded-lg p-3 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
          <div className="text-xs text-muted-foreground">
            <p className="font-medium">Merge Field Available</p>
            <p className="mt-1">
              Use <code className="bg-background px-1 py-0.5 rounded">{'{{substances_used}}'}</code> in case note templates to automatically insert the selected substances as a comma-separated list.
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default AddictionsScreeningInstrumentCard;
